import React, { useState, useEffect, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { Prayer } from './types/prayer';
import { fetchPrayerTimes, getNextPrayer } from './services/prayerService';
import { DEFAULT_DELEGATION_ID, DEFAULT_GOVERNORATE_ID } from './types/settings';
import { saveLocationSettings, loadLocationSettings } from './services/settingsService';
import PrayerCard from './components/PrayerCard';
import CountdownTimer from './components/CountdownTimer';
import Settings, { getLocationName } from './components/Settings';

// Arabic date formatting
const arabicDays = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const arabicMonths = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

const formatArabicDate = (date: Date): string => {
  const d = dayjs(date);
  const dayName = arabicDays[d.day()];
  const day = d.date();
  const month = arabicMonths[d.month()];
  const year = d.year();
  return `${dayName}، ${day} ${month} ${year}`;
};

function App() {
  const [prayers, setPrayers] = useState<Prayer[]>([]);
  const [nextDayPrayers, setNextDayPrayers] = useState<Prayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentPrayer, setCurrentPrayer] = useState<Prayer | null>(null);
  const [nextPrayer, setNextPrayer] = useState<Prayer | null>(null);
  const [delegationId, setDelegationId] = useState(DEFAULT_DELEGATION_ID);
  const [governoratId, setGovernoratId] = useState(DEFAULT_GOVERNORATE_ID);

  const locationInfo = useMemo(() => getLocationName(delegationId), [delegationId]);

  // Load saved settings on mount
  useEffect(() => {
    const savedSettings = loadLocationSettings();
    if (savedSettings) {
      setDelegationId(savedSettings.delegationId);
      setGovernoratId(savedSettings.governorateId || DEFAULT_GOVERNORATE_ID);
    }
  }, []);

  const isToday = useCallback((date: Date) => {
    return dayjs(date).format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
  }, []);

  const updatePrayerStatus = useCallback((prayersList: Prayer[], nextDayPrayersList: Prayer[]) => {
    if (!isToday(selectedDate)) {
      setCurrentPrayer(null);
      setNextPrayer(null);
      return;
    }

    const now = dayjs();
    const currentTime = now.format('HH:mm');

    const current = prayersList
      .find(prayer => {
        const nextPrayerIndex = prayersList.findIndex(p => p.id === prayer.id) + 1;
        const nextPrayer = prayersList[nextPrayerIndex] || { time: '23:59' };
        return prayer.time <= currentTime && currentTime < nextPrayer.time;
      });

    // If current prayer is Isha, get Fajr from next day
    if (current?.id === 'isha') {
      const nextDayFajr = nextDayPrayersList.find(p => p.id === 'sobh') || null;
      setNextPrayer(nextDayFajr);
    } else {
      const next = getNextPrayer(prayersList);
      setNextPrayer(next);
    }

    setCurrentPrayer(current || null);
  }, [isToday, selectedDate]);

  const loadPrayerTimes = useCallback(async (date: Date, cityId: number, govId: number) => {
    try {
      const times = await fetchPrayerTimes(date, cityId, govId);
      return times;
    } catch (err) {
      console.error('Error loading prayer times:', err);
      throw err;
    }
  }, []);

  useEffect(() => {
    const loadAllPrayerTimes = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load current day prayers
        const currentDayPrayers = await loadPrayerTimes(selectedDate, delegationId, governoratId);
        setPrayers(currentDayPrayers);

        // Load next day prayers only if viewing today
        if (isToday(selectedDate)) {
          const nextDay = dayjs(selectedDate).add(1, 'day').toDate();
          const nextDayResult = await loadPrayerTimes(nextDay, delegationId, governoratId);
          setNextDayPrayers(nextDayResult);
          updatePrayerStatus(currentDayPrayers, nextDayResult);
        } else {
          setNextDayPrayers([]);
          updatePrayerStatus(currentDayPrayers, []);
        }
      } catch (err) {
        setError('حدث خطأ في تحميل أوقات الصلاة. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    };

    loadAllPrayerTimes();
  }, [selectedDate, delegationId, loadPrayerTimes, isToday, updatePrayerStatus]);

  // Update current and next prayer status every minute only if viewing today
  useEffect(() => {
    if (!isToday(selectedDate)) return;

    const interval = setInterval(() => {
      updatePrayerStatus(prayers, nextDayPrayers);
    }, 60000);

    return () => clearInterval(interval);
  }, [prayers, nextDayPrayers, selectedDate, isToday, updatePrayerStatus]);

  const handleDelegationChange = (newDelegationId: number, newGovernoratId: number) => {
    setDelegationId(newDelegationId);
    setGovernoratId(newGovernoratId);
    saveLocationSettings({
      governorateId: newGovernoratId,
      delegationId: newDelegationId
    });
  };

  return (
    <div className="min-h-screen bg-gradient-islamic">
      {/* Safe area padding for mobile */}
      <div className="min-h-screen flex flex-col pt-safe-top relative z-10">
        {/* Header */}
        <header className="px-5 pt-5 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="text-3xl">🕌</span>
                <span>أوقات الصلاة</span>
              </h1>
            </div>

            {/* Location badge — top left (RTL end) */}
            <Settings
              currentDelegationId={delegationId}
              onDelegationChange={handleDelegationChange}
              renderTrigger={(openModal) => (
                <button
                  onClick={openModal}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 border border-emerald-500/25 rounded-full hover:bg-emerald-500/25 transition-all active:scale-95"
                >
                  <span className="text-xs">📍</span>
                  <span className="text-emerald-400 text-xs font-bold">
                    {locationInfo
                      ? `${locationInfo.delegationAr}`
                      : 'اختر الموقع'}
                  </span>
                  <svg className="w-3 h-3 text-emerald-400/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            />
          </div>
          <p className="text-white/50 text-sm mt-1">{formatArabicDate(selectedDate)}</p>

          {/* Date selector */}
          <div className="mt-4 flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setSelectedDate(new Date())}
              className={`px-5 py-2.5 rounded-2xl text-sm font-bold whitespace-nowrap transition-all ${isToday(selectedDate)
                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
            >
              اليوم
            </button>
            <input
              type="date"
              value={dayjs(selectedDate).format('YYYY-MM-DD')}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="modern-input flex-1 min-w-[140px] text-center"
            />
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 px-5 pb-8 space-y-4 overflow-y-auto">
          {error && (
            <div className="glass-card p-4 border-red-400/30 bg-red-500/10">
              <p className="text-red-300 text-sm text-center">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col justify-center items-center h-48 gap-3">
              <div className="spinner w-12 h-12"></div>
              <p className="text-white/60 text-sm">جاري التحميل...</p>
            </div>
          ) : (
            <>
              {isToday(selectedDate) && nextPrayer && (
                <CountdownTimer
                  nextPrayer={nextPrayer}
                  isNextDayFajr={currentPrayer?.id === 'isha'}
                />
              )}
              <div className="space-y-3">
                {prayers.map((prayer, index) => (
                  <div
                    key={prayer.id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <PrayerCard
                      prayer={prayer}
                      isNext={isToday(selectedDate) && nextPrayer?.id === prayer.id}
                      isCurrent={isToday(selectedDate) && currentPrayer?.id === prayer.id}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </main>

        {/* Footer */}
        <footer className="px-5 py-4 text-center">
          <p className="text-white/30 text-xs">
            صُنع بـ ❤️ لله
          </p>
        </footer>
      </div>
    </div>
  );
}

export default App;
